// ─────────────────────────────────────────────
//  PriceComparator.jsx — Calculadora Comparativa
//  Layout alineado con TipCalculator
// ─────────────────────────────────────────────

import React, { useState, useCallback } from 'react';
import appStyles from '../layout/CalculatorPage.module.css';
import styles from './PriceComparator.module.css';
import UnitPicker from './UnitPicker';
import ProductCard from './ProductCard';
import ResultStrip from './ResultStrip';
import EquivalenceCard from './EquivalenceCard';

const UNITS = [
	{ id: 'ml',   label: 'ml'   },
	{ id: 'l',    label: 'L'    },
	{ id: 'g',    label: 'g'    },
	{ id: 'kg',   label: 'kg'   },
	{ id: 'pzas', label: 'pzas' },
	{ id: 'oz',   label: 'oz'   },
];

// Colores por producto — usados en ProductCard y ResultStrip
export const PRODUCT_COLORS = {
	A: { dot: '#30D158', bg: 'rgba(48, 209, 88, 0.08)'   },
	B: { dot: '#FF9F0A', bg: 'rgba(255, 159, 10, 0.08)'  },
	C: { dot: '#BF5AF2', bg: 'rgba(191, 90, 242, 0.08)'  },
};

function formatPPU(value) {
	if (!value || value === 0) return '$0';
	if (value < 0.01)  return `$${value.toFixed(4)}`;
	if (value < 1)     return `$${value.toFixed(3)}`;
	return `$${value.toFixed(2)}`;
}

export default function PriceComparator() {
	const [priceA, setPriceA] = useState('');
	const [qtyA,   setQtyA]   = useState('');
	const [priceB, setPriceB] = useState('');
	const [qtyB,   setQtyB]   = useState('');
	const [showC,  setShowC]  = useState(false);
	const [priceC, setPriceC] = useState('');
	const [qtyC,   setQtyC]   = useState('');
	const [unit,   setUnit]   = useState('ml');

	// ── Parsed values ──────────────────────────
	const pA = parseFloat(priceA) || 0;
	const qA = parseFloat(qtyA)   || 0;
	const pB = parseFloat(priceB) || 0;
	const qB = parseFloat(qtyB)   || 0;
	const pC = parseFloat(priceC) || 0;
	const qC = parseFloat(qtyC)   || 0;

	const hasA = pA > 0 && qA > 0;
	const hasB = pB > 0 && qB > 0;
	const hasC = showC && pC > 0 && qC > 0;
	const hasData = hasA && hasB;

	// ── Price per unit ─────────────────────────
	const ppuA = hasA ? pA / qA : 0;
	const ppuB = hasB ? pB / qB : 0;
	const ppuC = hasC ? pC / qC : 0;

	// ── Winner ─────────────────────────────────
	const winner = hasData
		? (() => {
				const candidates = [
					{ id: 'A', ppu: ppuA },
					{ id: 'B', ppu: ppuB },
					...(hasC ? [{ id: 'C', ppu: ppuC }] : []),
				];
				return candidates.reduce((best, cur) =>
					cur.ppu < best.ppu ? cur : best
				).id;
			})()
		: null;

	// ── Savings ────────────────────────────────
	const winnerPPU = winner === 'A' ? ppuA : winner === 'B' ? ppuB : ppuC;
	const maxPPU    = hasData ? Math.max(ppuA, ppuB, ...(hasC ? [ppuC] : [])) : 0;

	const barFillPct = hasData && maxPPU > 0
		? (winnerPPU / maxPPU) * 100 : 0;

	const savingsPctA = hasData && winner !== 'A' && winnerPPU > 0
		? ((ppuA - winnerPPU) / winnerPPU) * 100 : 0;
	const savingsPctB = hasData && winner !== 'B' && winnerPPU > 0
		? ((ppuB - winnerPPU) / winnerPPU) * 100 : 0;
	const savingsPctC = hasData && hasC && winner !== 'C' && winnerPPU > 0
		? ((ppuC - winnerPPU) / winnerPPU) * 100 : 0;

	// ── Equivalences ───────────────────────────
	const worstLoser = hasData
		? [
				{ id: 'A', ppu: ppuA, price: pA, qty: qA },
				{ id: 'B', ppu: ppuB, price: pB, qty: qB },
				...(hasC ? [{ id: 'C', ppu: ppuC, price: pC, qty: qC }] : []),
			]
				.filter(p => p.id !== winner)
				.reduce((worst, cur) => cur.ppu > worst.ppu ? cur : worst)
		: null;

	const winnerQty   = hasData ? (winner === 'A' ? qA : winner === 'B' ? qB : qC) : 0;
	const winnerPrice = hasData ? (winner === 'A' ? pA : winner === 'B' ? pB : pC) : 0;
	const loserQty    = worstLoser ? worstLoser.qty   : 0;
	const loserPrice  = worstLoser ? worstLoser.price : 0;

	const equivalentUnits  = hasData && winnerQty > 0 ? loserQty / winnerQty : 0;
	const costOfEquivalent = hasData ? equivalentUnits * winnerPrice : 0;
	const savingsAmount    = hasData ? loserPrice - costOfEquivalent : 0;

	// ── Savings text ───────────────────────────
	const savingsText = hasData
		? hasC
			? (() => {
					const ranked = [
						{ id: 'A', ppu: ppuA },
						{ id: 'B', ppu: ppuB },
						{ id: 'C', ppu: ppuC },
					].sort((a, b) => a.ppu - b.ppu);
					const diff = ranked[1].ppu - ranked[0].ppu;
					return `Producto ${winner} es el más barato — ${formatPPU(diff)} menos por ${unit} que el siguiente`;
				})()
			: `Producto ${winner} es ${formatPPU(Math.abs(ppuA - ppuB))} más barato por ${unit}${
					savingsAmount > 0.005
						? ` — ahorras $${savingsAmount.toFixed(2)} en cantidad equivalente`
						: ''
				}`
		: '';

	// ── Handlers ───────────────────────────────
	const handleReset = useCallback(() => {
		setPriceA(''); setQtyA('');
		setPriceB(''); setQtyB('');
		setPriceC(''); setQtyC('');
		setShowC(false);
		setUnit('ml');
	}, []);

	const handleRemoveC = useCallback(() => {
		setShowC(false); setPriceC(''); setQtyC('');
	}, []);

	const anyFilled = priceA || qtyA || priceB || qtyB || priceC || qtyC;

	return (
		<main className={appStyles.root} aria-label="Calculadora comparativa de precios">

			{/* ── Top Bar ── */}
			<div className={appStyles.topBar}>
				<div className={styles.topRow}>
					<p className={styles.unitLabel}>Unidad de medida</p>
					{anyFilled && (
						<button
							type="button"
							className={styles.resetBtn}
							onClick={handleReset}
							aria-label="Limpiar todos los campos"
						>
							<span aria-hidden="true">↺</span> Reiniciar
						</button>
					)}
				</div>
				<UnitPicker units={UNITS} active={unit} onSelect={setUnit} />
			</div>

			{/* ── Productos ── */}
			<div className={styles.products}>
			<ProductCard
				product="A"
				colors={PRODUCT_COLORS.A}
				price={priceA}
				qty={qtyA}
				unit={unit}
				onPriceChange={setPriceA}
				onQtyChange={setQtyA}
				winner={winner === 'A'}
				loser={hasData && winner !== 'A'}
				savingsPct={savingsPctA}
				hasData={hasData}
			/>

			<ProductCard
				product="B"
				colors={PRODUCT_COLORS.B}
				price={priceB}
				qty={qtyB}
				unit={unit}
				onPriceChange={setPriceB}
				onQtyChange={setQtyB}
				winner={winner === 'B'}
				loser={hasData && winner !== 'B'}
				savingsPct={savingsPctB}
				hasData={hasData}
			/>

			{showC ? (
				<ProductCard
					product="C"
					colors={PRODUCT_COLORS.C}
					price={priceC}
					qty={qtyC}
					unit={unit}
					onPriceChange={setPriceC}
					onQtyChange={setQtyC}
					winner={winner === 'C'}
					loser={hasData && hasC && winner !== 'C'}
					savingsPct={savingsPctC}
					hasData={hasData && hasC}
					onRemove={handleRemoveC}
				/>
			) : (
				<button
					type="button"
					className={styles.addProductBtn}
					onClick={() => setShowC(true)}
					aria-label="Agregar tercer producto para comparar"
				>
					<span className={styles.addIcon} aria-hidden="true">+</span>
					Agregar Producto C
				</button>
			)}
			</div>

			{/* ── Result strip ── */}
			<ResultStrip
				ppuA={ppuA}
				ppuB={ppuB}
				ppuC={ppuC}
				unit={unit}
				winner={winner}
				barFillPct={barFillPct}
				savingsText={savingsText}
				hasData={hasData}
				showC={showC}
				hasC={hasC}
			/>

			{/* ── Equivalencias ── */}
			{hasData && (
				<EquivalenceCard
					winner={winner}
					equivalentUnits={equivalentUnits}
					costOfEquivalent={costOfEquivalent}
					savingsAmount={savingsAmount}
					winnerPrice={winnerPrice}
					loserPrice={loserPrice}
					loserLabel={worstLoser ? worstLoser.id : ''}
					unit={unit}
				/>
			)}

		</main>
	);
}
